import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useCurrentFamilyContext } from 'src/clients/context/useCurrentFamilyContext';
import { useClient } from 'src/clients/hooks/useClient';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useAppContainerContext } from './AppContainerContext';
import { useChatMessages } from './chat/hooks/useChatMessages';
import {
  ChatMode,
  ChatState,
  ErrorMarker,
  Message,
  ProposedActionStatus,
  RetryPayload,
  StoredMessage,
} from './chat/types';
import { openConversationFromServer } from './chats/openConversationFromServer';
import { ChatFile } from './chats/types';
import { PreviousChatContext } from './sidebar/DisplacedChatBanner';
import { useDisplacedChat } from './sidebar/useDisplacedChat';
import { useAppContainer } from './useAppContainer';
import { resolveDocumentEditOwner } from 'src/document-editor/documentOwner';
import { useSiestaPrepStore } from './useSiestaPrepStore';

type ChatScope = {
  familyModeActive: boolean;
  familyId: string | null;
  clientId: string | null;
  focusedClientId: string | null;
};

type SiestaContextValue = {
  messages: Message[];
  latestIncomingId: string | null;
  currentChatState: ChatState | null;
  isReconnecting: boolean;
  previousChatContext: PreviousChatContext | null;
  usedActionIds: Set<string>;
  chatScope: ChatScope;
  sendUserMessage: (
    content: string,
    clientId: string | null,
    conversationId: string,
    attachedFiles?: ChatFile[],
    verify?: { sectionPath?: string[] },
    silent?: boolean,
    notificationId?: string,
  ) => Promise<void>;
  retryErroredSend: (marker: ErrorMarker) => Promise<void>;
  resumeActiveTurn: (conversationId: string, turnId: string, retryPayload?: RetryPayload) => void;
  clearMessages: () => void;
  loadHistory: (rawMessages: StoredMessage[]) => void;
  isLoadingHistory: boolean;
  beginHistoryLoad: () => void;
  endHistoryLoad: () => void;
  replaceLastAssistantMessage: (content: string) => void;
  appendInterruptedMarker: () => void;
  appendFailedMarker: () => void;
  finaliseLimitReachedMessage: (id: string) => void;
  markProposalDecided: (
    messageId: string,
    proposalId: string,
    status: ProposedActionStatus,
    failureReason?: string | null,
  ) => void;
  supersedePendingProposals: () => void;
  cancelStreaming: () => void;
  stopGeneration: () => void;
  resetIfDisplaced: () => string;
  discardChat: () => string;
  adoptDisplacedChat: () => void;
  goBack: () => void;
  markActionUsed: (id: string) => void;
  clearUsedActionIds: () => void;
};

const SiestaContext = createContext<SiestaContextValue | undefined>(undefined);

export function SiestaProvider({ children }: { children: React.ReactNode }) {
  const { contentMeta, setConversationId } = useAppContainerContext();
  const { siestaMode, setSiestaMode } = useAppContainer();
  const {
    flagEnabled,
    clientId: routeClientId,
    householdFamilyId,
    isHouseholdView,
  } = useCurrentFamilyContext();

  // On a member's own page, resolve their household so the chat runs over the whole family with that
  // member in focus. The conversation is family-scoped from the first message, so moving to the
  // family view (or another member) continues the same thread — nothing is reparented.
  // Mirrors useCurrentClientIfAvailable: the single-client fetch only carries familyId when
  // paginated clients is on, so fall back to the client list otherwise.
  const {
    flagEnabledPaginatedClients,
    flagEnabledSiestaIndividualChatScope,
    flagEnabledSiestaChats,
  } = useFlags();
  const wantsClientFamily = Boolean(flagEnabled && !isHouseholdView && routeClientId);
  const single = useClient(
    wantsClientFamily && flagEnabledPaginatedClients ? (routeClientId ?? undefined) : undefined,
  );
  const list = useClients({}, { enabled: wantsClientFamily && !flagEnabledPaginatedClients });
  const resolvedClient = flagEnabledPaginatedClients
    ? single.client
    : (list.clients.find(c => c.id === routeClientId) ?? null);
  const clientFamilyId = wantsClientFamily ? (resolvedClient?.familyId ?? null) : null;
  const resolvedFamilyId = isHouseholdView ? householdFamilyId : clientFamilyId;

  // A member's client detail can be briefly uncached on first navigation, leaving its household
  // momentarily unknown. Hold the last known household across that gap so navigating within a family
  // doesn't flicker the chat's scope and wrongly displace the thread — but only onto that household's
  // own members: bridging to an unrelated client must not briefly scope the chat to the previous
  // family. While the held family itself is loading (it's cached from the chat just held), keep it
  // rather than flicker.
  const clientResolving =
    wantsClientFamily && (flagEnabledPaginatedClients ? single.isLoading : list.isLoading);
  const lastFamilyIdRef = useRef<string | null>(null);
  const heldFamilyId = clientResolving ? lastFamilyIdRef.current : null;
  const { family: heldFamily } = useFamily(heldFamilyId ?? undefined);
  const heldCoversRoute =
    heldFamilyId !== null &&
    (routeClientId === null ||
      heldFamily === null ||
      heldFamily.members.some(member => member.clientId === routeClientId));
  const familyId = resolvedFamilyId ?? (heldCoversRoute ? heldFamilyId : null);
  useEffect(() => {
    lastFamilyIdRef.current = familyId;
  }, [familyId]);

  const familyModeActive = Boolean(flagEnabled && familyId);
  // A member's page chats as that member, with the backend supplying the household as context, so
  // the conversation is filed against them. Only the whole-household view runs a family chat.
  const householdChatActive =
    familyModeActive && (isHouseholdView || !flagEnabledSiestaIndividualChatScope);
  // When focused on a member's page (not the whole-household view), tell Siesta which member so bare
  // questions default to them.
  const focusedClientId = householdChatActive && !isHouseholdView ? routeClientId : null;
  const clientId = householdChatActive ? null : routeClientId;

  const isDocumentMode = contentMeta?.chatMode === ChatMode.DOCUMENT;
  const documentOwner = resolveDocumentEditOwner(routeClientId, householdFamilyId);
  const documentClientId = documentOwner?.clientId ?? null;
  const documentFamilyId = documentOwner?.familyId ?? null;
  const requestBodyBuilder = (
    message: string,
    activeConversationId: string,
    verify?: { sectionPath?: string[] },
  ) => {
    if (!documentOwner) throw new Error('Document edit owner is required');
    return {
      conversationId: activeConversationId,
      documentContent:
        contentMeta?.getDocumentContent?.() ?? contentMeta?.metadata?.documentContent ?? '',
      editInstruction: message,
      ...documentOwner,
      ...(verify ? { verifyClaim: message, verifySectionPath: verify.sectionPath } : {}),
    };
  };
  const resolveChatOptions = (): Parameters<typeof useChatMessages>[0] => {
    if (isDocumentMode) {
      return {
        mode: ChatMode.DOCUMENT,
        entityId: contentMeta?.entityId ?? '',
        onStateChange: contentMeta?.onChatStateChange,
        requestBodyBuilder,
      };
    }
    if (householdChatActive && familyId) {
      return {
        mode: ChatMode.FAMILY,
        familyId,
        focusedClientId,
        onStateChange: contentMeta?.onChatStateChange,
      };
    }
    return { mode: ChatMode.CLIENT, onStateChange: contentMeta?.onChatStateChange };
  };
  const chat = useChatMessages(resolveChatOptions());

  const [usedActionIds, setUsedActionIds] = useState<Set<string>>(new Set());
  const markActionUsed = (id: string) => setUsedActionIds(prev => new Set(prev).add(id));
  const clearUsedActionIds = () => setUsedActionIds(new Set());

  let trackingOwnerId = clientId;
  let trackingChatMode = contentMeta?.chatMode;
  if (householdChatActive) {
    trackingOwnerId = familyId;
    trackingChatMode = ChatMode.FAMILY;
  }
  if (isDocumentMode) {
    trackingOwnerId = documentClientId ?? documentFamilyId;
    trackingChatMode = ChatMode.DOCUMENT;
  }
  const returnToConversation = (returnedConversationId: string) =>
    openConversationFromServer(returnedConversationId, {
      currentConversationId: returnedConversationId,
      flagEnabledSiestaChats,
      loadHistory: chat.loadHistory,
      beginHistoryLoad: chat.beginHistoryLoad,
      endHistoryLoad: chat.endHistoryLoad,
      resumeActiveTurn: chat.resumeActiveTurn,
      appendFailedMarker: chat.appendFailedMarker,
      setConversationId,
      setSiestaMode,
    });

  const tracking = useDisplacedChat({
    messageCount: chat.messages.length,
    clientId: trackingOwnerId ?? undefined,
    chatMode: trackingChatMode,
    entityId: isDocumentMode
      ? contentMeta?.entityId
      : ((householdChatActive ? familyId : contentMeta?.entityId) ?? undefined),
    clearMessages: chat.clearMessages,
    clearUsedActionIds,
    cancelStreaming: chat.cancelStreaming,
    onReturnToConversation: returnToConversation,
  });

  const chatScope: ChatScope = isDocumentMode
    ? {
        familyModeActive: documentFamilyId !== null,
        familyId: documentFamilyId,
        clientId: documentClientId,
        focusedClientId: null,
      }
    : { familyModeActive: householdChatActive, familyId, clientId, focusedClientId };

  // The scope above is assembled from three sources that land in separate commits: the route's
  // client, the family query behind it, and contentMeta, which the destination page only sets once
  // mounted. Send before all three have reported and the last one to arrive reads as a scope change
  // with the prep's messages already on screen, which useDisplacedChat cancels as a displaced chat.
  // discardChat() returns the fresh conversation id directly rather than through state, and the mode
  // is set unconditionally, since both are still pre-navigation in this commit.
  const prep = useSiestaPrepStore(state => state.prep);
  const consumePrep = useSiestaPrepStore(state => state.consumePrep);
  useEffect(() => {
    if (!prep || routeClientId !== prep.clientId) return;
    if (clientResolving || contentMeta === null) return;
    consumePrep();
    setSiestaMode('chat');
    void chat.sendUserMessage(
      prep.anchor,
      clientId,
      tracking.discardChat(),
      undefined,
      undefined,
      undefined,
      prep.notificationId,
    );
  }, [
    prep,
    routeClientId,
    clientResolving,
    contentMeta,
    clientId,
    consumePrep,
    setSiestaMode,
    chat.sendUserMessage,
    tracking.discardChat,
  ]);

  useEffect(() => {
    if (siestaMode !== 'chat' && siestaMode !== 'busy') {
      chat.cancelStreaming();
    }
  }, [siestaMode, chat.cancelStreaming]);

  const chatActionsRef = contentMeta?.chatActionsRef;
  useEffect(() => {
    if (chatActionsRef) {
      chatActionsRef.current = {
        replaceLastAssistantMessage: chat.replaceLastAssistantMessage,
        // resetIfDisplaced gives the active conversation id (same discipline as the other sends).
        sendVerifyMessage: (claim, sectionPath) => {
          setSiestaMode('chat');
          void chat.sendUserMessage(claim, chatScope.clientId, tracking.resetIfDisplaced(), [], {
            sectionPath,
          });
        },
      };
    }
    return () => {
      if (chatActionsRef) chatActionsRef.current = null;
    };
  }, [
    chatActionsRef,
    chat.replaceLastAssistantMessage,
    chat.sendUserMessage,
    tracking.resetIfDisplaced,
    chatScope.clientId,
    setSiestaMode,
  ]);

  return (
    <SiestaContext
      value={{ ...chat, ...tracking, usedActionIds, markActionUsed, clearUsedActionIds, chatScope }}
    >
      {children}
    </SiestaContext>
  );
}

export function useSiesta() {
  const ctx = useContext(SiestaContext);
  if (ctx === undefined) {
    throw new Error('useSiesta must be used within a SiestaProvider');
  }
  return ctx;
}
